import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Plus, Minus, Trash2, ScanLine, User, Printer, Pause, Play, ShoppingCart, X,
  CheckCircle2, Wifi, WifiOff, ReceiptText, RefreshCw, Boxes, ArrowLeft, Loader2, ClipboardCheck,
  MessageCircle, Truck, Percent,
} from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { inr, uid, statusTone, dmy } from "@/lib/format";
import { printReceipt, printInvoice } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import BdsLogo from "@/components/BdsLogo";
import QrScanner from "@/components/QrScanner";
import usePageTitle from "@/hooks/usePageTitle";
import {
  readQueue, writeQueue, saveCache, loadCache, nextOfflineNo,
  computeBillLocal, buildOfflineInvoice,
} from "@/lib/offline";
import { shareOnWhatsapp } from "@/lib/whatsapp";
import IstClock from "@/components/IstClock";
import { fx } from "@/components/ActionFx";

const PAYMENTS = ["Cash", "UPI", "Card", "Bank Transfer", "Credit", "Mixed"];

export default function POS() {
  usePageTitle("Billing");
  const navigate = useNavigate();
  const { settings, user, can } = useAuth();
  const shop = settings?.shop;

  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [customer, setCustomer] = useState({ mobile: "", name: "", whatsapp: "", email: "", address: "" });
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [method, setMethod] = useState("Cash");
  const [splits, setSplits] = useState([{ method: "Cash", amount: "" }]);
  const [paid, setPaid] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [held, setHeld] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [todayBills, setTodayBills] = useState([]);
  const [showBills, setShowBills] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(readQueue().length);
  const [showPending, setShowPending] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stockView, setStockView] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanMsg, setScanMsg] = useState("");
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditScan, setAuditScan] = useState(false);
  const [auditLines, setAuditLines] = useState([]);
  const [auditBusy, setAuditBusy] = useState(false);
  const [delivery, setDelivery] = useState({ on: false, charge: "", address: "", landmark: "", notes: "" });
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [agentLocked, setAgentLocked] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [agentBlocked, setAgentBlocked] = useState("");
  const payCfg = settings?.payment || {};
  const [payDetails, setPayDetails] = useState({});
  const [discount, setDiscount] = useState({ mode: "fixed", value: "", reason: "" });
  const [discountCtx, setDiscountCtx] = useState(null);
  const [autoRules, setAutoRules] = useState([]);

  const setDetail = (m, key, value) =>
    setPayDetails((prev) => ({ ...prev, [m]: { ...(prev[m] || {}), [key]: value } }));

  const activeMethods =
    method === "Mixed"
      ? splits.filter((s) => Number(s.amount || 0) > 0).map((s) => s.method)
      : [method];

  const [preview, setPreview] = useState(null);
  const searchRef = useRef(null);
  const printer = settings?.printer || {};
  const whatsappCfg = settings?.whatsapp || {};

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await api.get("/products", { params: { active: true, limit: 1000 } });
      setProducts(data.items);
      setFromCache(false);
      return data.items;
    } catch (e) {
      const cache = loadCache();
      if (cache?.products?.length) {
        setProducts(cache.products);
        setFromCache(true);
        return cache.products;
      }
      throw e;
    }
  }, []);

  useEffect(() => {
    loadProducts().catch(() => toast.error("Could not load products and no offline cache is available yet."));
    api.get("/categories").then(({ data }) => setCategories(data.map((c) => c.name))).catch(() => {
      const cache = loadCache();
      if (cache?.categories) setCategories(cache.categories);
    });
    api.get("/held-bills").then(({ data }) => setHeld(data)).catch(() => {});
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    searchRef.current?.focus();
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [loadProducts]);

  // keep the offline catalog snapshot fresh whenever we are online
  useEffect(() => {
    if (products.length && categories.length && settings && !fromCache) {
      saveCache({ products, categories, settings, rules: autoRules });
    }
  }, [products, categories, settings, fromCache, autoRules]);

  // quote recalculation (debounced)
  useEffect(() => {
    if (!cart.length) {
      setQuote(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoadingQuote(true);
      try {
        const { data } = await api.post("/pos/quote", {
          items: cart.map((c) => ({ product_oid: c.id, qty: c.qty })),
          manual_discount_mode: discount.mode,
          manual_discount_value: Number(discount.value || 0),
          manual_discount_reason: discount.reason,
        });
        setQuote(data);
      } catch (e) {
        if (!e?.response?.status) {
          const cfg = loadCache()?.settings || settings;
          setQuote(computeBillLocal(cart, cfg, 0, loadCache()?.rules || autoRules));
        } else {
          toast.error(errMsg(e));
        }
      } finally {
        setLoadingQuote(false);
      }
    }, 180);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, discount.mode, discount.value, discount.reason]);

  useEffect(() => {
    api.get("/pos/discount-context").then(({ data }) => setDiscountCtx(data)).catch(() => {});
    api.get("/discount-rules/active").then(({ data }) => setAutoRules(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (quote && method === "Mixed") setSplits((s) => (s.length ? s : [{ method: "Cash", amount: "" }]));
  }, [quote, method]);

  const activeCategories = useMemo(() => {
    const live = new Set(products.map((p) => p.category).filter(Boolean));
    return categories.filter((c) => live.has(c));
  }, [categories, products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (!q) return true;
      return [p.name, p.sku, p.stk_code, p.barcode, p.product_id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [products, query, category]);

  const addToCart = (p) => {
    if (p.stock <= 0) return toast.error(`${p.name} is out of stock.`);
    setCart((prev) => {
      const found = prev.find((c) => c.id === p.id);
      if (found) {
        if (found.qty + 1 > p.stock) {
          toast.error(`Only ${p.stock} units are available in stock.`);
          return prev;
        }
        return prev.map((c) => (c.id === p.id ? { ...c, qty: c.qty + 1 } : c));
      }
      return [...prev, { id: p.id, product: p, qty: 1 }];
    });
    setQuery("");
    searchRef.current?.focus();
  };

  const changeQty = (id, delta, absolute) => {
    setCart((prev) =>
      prev
        .map((c) => {
          if (c.id !== id) return c;
          if (absolute === "") return { ...c, qty: "", error: false };
          const next = absolute != null ? absolute : Number(c.qty || 0) + delta;
          if (next > c.product.stock) {
            toast.error(`Only ${c.product.stock} units are available in stock.`);
            return { ...c, qty: c.product.stock, error: true };
          }
          return { ...c, qty: Math.max(0, next), error: false };
        })
        .filter((c) => c.qty === "" || c.qty > 0)
    );
  };

  const onSearchKey = (e) => {
    if (e.key === "Enter") {
      const q = query.trim().toLowerCase();
      if (!q) return;
      const exact = products.find(
        (p) => [p.barcode, p.sku, p.stk_code, p.product_id].filter(Boolean).some((v) => String(v).toLowerCase() === q)
      );
      const target = exact || filtered[0];
      if (target) addToCart(target);
      else toast.error("Product not found.");
    }
  };

  const handleScan = async (code) => {
    try {
      const { data } = await api.get("/scan-product", { params: { code } });
      if (!data.sellable) {
        const msg = data.message || "This product is currently out of stock.";
        setScanMsg("");
        toast.error(msg);
        return;
      }
      const p = data.product;
      const local = products.find((x) => x.id === p.id) || p;
      const label = p.stk_code || p.product_id;
      const existing = cart.find((c) => c.id === p.id);
      if (existing && existing.qty + 1 > p.stock) {
        setScanMsg(`Only ${p.stock} units of this product are available.`);
        toast.error(`Only ${p.stock} units of this product are available.`);
        return;
      }
      addToCart(local);
      const msg = existing
        ? `${label} quantity increased to ${existing.qty + 1}.`
        : `${label} added to bill.`;
      setScanMsg(msg);
      toast.success(msg);
      fx.added(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addAuditLine = async (code) => {
    try {
      const { data } = await api.get("/scan-product", { params: { code } });
      const p = data.product;
      setAuditScan(false);
      setAuditLines((prev) =>
        prev.some((l) => l.product_oid === p.id)
          ? prev
          : [...prev, { product_oid: p.id, name: p.name, stk_code: p.stk_code, counted: "" }]
      );
      toast.success(`${p.name} added to count sheet`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submitAudit = async () => {
    const lines = auditLines
      .filter((l) => l.counted !== "" && Number(l.counted) >= 0)
      .map((l) => ({ product_oid: l.product_oid, counted: Number(l.counted) }));
    if (!lines.length) return toast.error("Enter the counted quantity for at least one product.");
    setAuditBusy(true);
    try {
      const { data } = await api.post("/stock/counts", { lines, counted_by: "Counter Staff" });
      toast.success(`${data.count_id} submitted for administrator approval.`);
      setAuditLines([]);
      setAuditOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAuditBusy(false);
    }
  };


  const lookupCustomer = async (mobile) => {
    setCustomer((c) => ({ ...c, mobile }));
    if (mobile.replace(/\D/g, "").length < 10) {
      setFoundCustomer(null);
      return;
    }
    try {
      const { data } = await api.get("/customers/lookup", { params: { mobile } });
      if (data.found) {
        setFoundCustomer(data.customer);
        setCustomer((c) => ({
          ...c,
          name: data.customer.name,
          whatsapp: data.customer.whatsapp || "",
          email: data.customer.email || "",
          address: data.customer.address || "",
        }));
        toast.success(`Customer found: ${data.customer.name}`);
      } else setFoundCustomer(null);
    } catch {}
  };

  const overselling = cart.some((c) => c.qty > c.product.stock);
  const grandBase = (quote?.grand_total || 0) + (delivery.on ? Number(delivery.charge || 0) : 0);
  const productTotal = quote?.grand_total || 0;
  const splitTotal = splits.reduce((s, x) => s + Number(x.amount || 0), 0);
  const paidAmount = method === "Mixed" ? splitTotal : Number(paid || 0);
  const cardFee = (() => {
    if (payCfg.card_fee_enabled === false) return { amount: 0, rate: 0, type: "" };
    const rates = {
      "Credit Card": Number(payCfg.credit_card_fee_rate ?? 2),
      "Debit Card": Number(payCfg.debit_card_fee_rate ?? 0.9),
    };
    let amount = 0;
    let rate = 0;
    let type = "";
    activeMethods.forEach((m) => {
      if (m !== "Card") return;
      const d = payDetails[m] || {};
      const r = rates[d.card_type] || 0;
      if (!r) return;
      const base =
        method === "Mixed"
          ? Math.min(Number(splits.find((s) => s.method === "Card")?.amount || 0), Number(grandBase || 0))
          : Number(grandBase || 0);
      amount += (base * r) / 100;
      rate = r;
      type = d.card_type;
    });
    return { amount: Math.round(amount * 100) / 100, rate, type };
  })();
  const grand = (settings?.invoice?.round_off === false)
    ? Math.round((grandBase + cardFee.amount) * 100) / 100
    : Math.round(grandBase + cardFee.amount);
  const balance = Math.max(0, grand - paidAmount);
  const change = Math.max(0, paidAmount - grand);

  // keep "amount received" in step with the fee-inclusive total for single-method payments
  useEffect(() => {
    if (!cart.length) return setPaid("");
    if (quote && method !== "Credit" && method !== "Mixed") setPaid(String(grand));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grand, method, quote?.grand_total, cart.length]);

  const reset = () => {
    setCart([]);
    setQuote(null);
    setCustomer({ mobile: "", name: "", whatsapp: "", email: "", address: "" });
    setFoundCustomer(null);
    setMethod("Cash");
    setSplits([{ method: "Cash", amount: "" }]);
    setPaid("");
    setDueDate("");
    setDelivery({ on: false, charge: "", address: "", landmark: "", notes: "" });
    setDiscount({ mode: "fixed", value: "", reason: "" });
    api.get("/pos/discount-context").then(({ data }) => setDiscountCtx(data)).catch(() => {});
    setShowCart(false);
    searchRef.current?.focus();
  };

  const completeBill = async () => {
    if (!cart.length) return toast.error("Cart is empty.");
    if (overselling) return toast.error("Fix quantities exceeding available stock.");
    if (method === "Mixed" && Math.abs(splitTotal - 0) === 0) return toast.error("Enter payment breakup.");
    if (method === "Credit" && !customer.mobile) return toast.error("Customer mobile is required for credit sale.");
    if (agentBlocked) return toast.error(agentBlocked);
    if (quote?.manual_discount_error) return toast.error(quote.manual_discount_error);
    if (!user && !agentId) {
      fx.error("Please select a Billing Agent");
      return toast.error("Please select a Billing Agent before completing the bill.");
    }
    const payload = {
      payment_details_map: payDetails,
      billing_agent_id: agentId || null,
      order_type: delivery.on ? "DELIVERY" : "COUNTER",
      delivery_required: !!delivery.on,
      delivery_charge: delivery.on ? Math.max(0, Number(delivery.charge || 0)) : 0,
      delivery_address: delivery.on ? delivery.address : "",
      delivery_landmark: delivery.on ? delivery.landmark : "",
      delivery_notes: delivery.on ? delivery.notes : "",
      items: cart.map((c) => ({ product_oid: c.id, qty: c.qty })),
      manual_discount_mode: discount.mode,
      manual_discount_value: Number(discount.value || 0),
      manual_discount_reason: discount.reason,
      customer_name: customer.name,
      customer_mobile: customer.mobile,
      customer_whatsapp: customer.whatsapp,
      customer_email: customer.email,
      customer_address: customer.address,
      payment_method: method,
      payments: method === "Mixed" ? splits.map((s) => ({ method: s.method, amount: Number(s.amount || 0) })) : [],
      amount_paid: method === "Credit" ? Number(paid || 0) : paidAmount,
      due_date: dueDate || null,
      idempotency_key: uid(),
      client_txn_id: uid(),
      cashier: user?.username || "Shop Keeper",
    };
    setBusy(true);
    try {
      const { data } = await api.post("/invoices", payload);
      const inv = data.invoice;
      setLastInvoice(inv);
      toast.success(`Invoice ${inv.invoice_no} completed successfully.`);
      fx.bill(inv);
      if (printer.auto_print !== false) printReceipt(inv, shop, printer, undefined, false);
      setPreview(inv);
      reset();
      loadProducts();
    } catch (e) {
      const status = e?.response?.status;
      if (!status) {
        // no network: bill offline with a temporary number, print immediately, sync later
        const cfg = loadCache()?.settings || settings;
        const bill = computeBillLocal(cart, cfg, 0, loadCache()?.rules || autoRules);
        const offlineNo = nextOfflineNo();
        const inv = buildOfflineInvoice({ bill, payload, offlineNo, cashier: payload.cashier });
        const q = readQueue();
        q.push({ payload, offline_no: offlineNo, grand_total: bill.grand_total, queued_at: new Date().toISOString() });
        writeQueue(q);
        setPending(q.length);
        setProducts((prev) =>
          prev.map((p) => {
            const line = cart.find((c) => c.id === p.id);
            return line ? { ...p, stock: Math.max(0, Number(p.stock) - line.qty) } : p;
          })
        );
        setLastInvoice(inv);
        if (printer.auto_print !== false) printReceipt(inv, shop, printer, undefined, false);
        setPreview(inv);
        reset();
        toast.success(`Offline bill ${offlineNo} completed. It will sync automatically when the internet is back.`);
        try {
          await api.post("/offline/queue", { client_txn_id: payload.client_txn_id, payload, error: "network" });
        } catch {}
      } else {
        toast.error(errMsg(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const syncQueue = useCallback(async () => {
    const q = readQueue();
    if (!q.length) return;
    setSyncing(true);
    const remaining = [];
    for (const entry of q) {
      const payload = entry.payload || entry;
      try {
        const { data } = await api.post("/invoices", payload);
        toast.success(`${entry.offline_no || "Offline bill"} synced as ${data.invoice.invoice_no}.`);
      } catch (e) {
        if (!e?.response?.status) remaining.push(entry);
        else {
          toast.error(`${entry.offline_no || "Offline bill"} could not sync: ${errMsg(e)}`);
          remaining.push({ ...entry, error: errMsg(e) });
        }
      }
    }
    writeQueue(remaining);
    setPending(remaining.length);
    setSyncing(false);
    loadProducts().catch(() => {});
  }, [loadProducts]);

  useEffect(() => {
    if (online && pending > 0) syncQueue();
  }, [online, pending, syncQueue]);

  const holdBill = async () => {
    if (!cart.length) return toast.error("Cart is empty.");
    const { data } = await api.post("/held-bills", {
      label: customer.name || `Cart ${cart.length} items`,
      payload: { cart: cart.map((c) => ({ id: c.id, qty: c.qty })), customer, discount },
    });
    setHeld((h) => [data, ...h]);
    toast.success("Bill held. Resume anytime.");
    reset();
  };

  const resumeBill = async (h) => {
    const items = h.payload.cart
      .map((c) => {
        const p = products.find((x) => x.id === c.id);
        return p ? { id: p.id, product: p, qty: Math.min(c.qty, p.stock) } : null;
      })
      .filter(Boolean);
    setCart(items);
    setCustomer(h.payload.customer || { mobile: "", name: "" });
    setDiscount(h.payload.discount || { mode: "fixed", value: "", reason: "" });
    await api.delete(`/held-bills/${h.id}`);
    setHeld((prev) => prev.filter((x) => x.id !== h.id));
    toast.success("Held bill resumed.");
  };

  // billing agent: auto for a logged-in user, mandatory dropdown at a no-login counter
  useEffect(() => {
    if (user === null) return;
    const canChange = !user || can("billing_agents.change");
    api
      .get("/billing-agents/active")
      .then(({ data }) => {
        setAgents(data);
        if (!user) {
          const def = data.find((a) => a.is_default);
          if (def) setAgentId(def.id);
        }
      })
      .catch(() => {});
    if (user) {
      api
        .get("/me/billing-agent")
        .then(({ data }) => {
          if (data.agent) {
            setAgentId(data.agent.id);
            setAgentName(data.agent.agent_name);
            setAgentLocked(!canChange);
          } else if (data.message) {
            setAgentBlocked(data.message);
          } else {
            setAgentName(data.fallback_name || "");
            setAgentLocked(!canChange);
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadTodayBills = async () => {
    try {
      const { data } = await api.get("/invoices", { params: { today_only: true, limit: 100 } });
      setTodayBills(data.items);
      setShowBills(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const CartRows = () => (
    <div className="space-y-2">
      {cart.length === 0 && <p className="py-8 text-center text-sm text-slate-400">Scan or search a product to begin.</p>}
      {cart.map((c) => {
        const line = quote?.items?.find((i) => i.product_oid === c.id);
        const bad = c.qty > c.product.stock;
        return (
          <div
            key={c.id}
            data-testid={`cart-row-${c.id}`}
            className={`rounded-xl border p-3 ${bad ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{c.product.name}</p>
                <p className="text-[11px] text-slate-500">
                  {c.product.stk_code || c.product.sku} · {inr(c.product.selling_price)} · stock {c.product.stock}
                  {line?.discount_eligible && (
                    <span className="ml-1 rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">discount eligible</span>
                  )}
                </p>
                {Number(line?.auto_discount) > 0 && (
                  <p data-testid={`cart-auto-disc-${c.id}`} className="text-[11px] font-semibold text-emerald-700">
                    Auto Disc −{inr(line.auto_discount)}{line.discount_rule ? ` · ${line.discount_rule}` : ""}
                  </p>
                )}
              </div>
              <button data-testid={`cart-remove-${c.id}`} onClick={() => changeQty(c.id, 0, 0)} className="text-slate-400 hover:text-rose-600">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  data-testid={`qty-minus-${c.id}`}
                  onClick={() => changeQty(c.id, -1)}
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white transition-transform active:scale-95"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  data-testid={`qty-input-${c.id}`}
                  value={c.qty}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // an empty box means "I'm retyping" — never drop the line for that
                    if (raw === "") return changeQty(c.id, 0, "");
                    changeQty(c.id, 0, Number(raw));
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "" || Number(e.target.value) <= 0) changeQty(c.id, 0, 1);
                  }}
                  className="h-11 w-16 rounded-xl border border-slate-200 text-center text-sm tnum outline-none focus:border-indigo-400"
                />
                <button
                  data-testid={`qty-plus-${c.id}`}
                  onClick={() => changeQty(c.id, 1)}
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white transition-transform active:scale-95"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <p className="font-outfit text-base font-semibold tnum text-slate-900">
                {inr(line ? line.line_total : c.qty * c.product.selling_price)}
              </p>
            </div>
            {bad && <p className="mt-1 text-xs font-semibold text-rose-600">Only {c.product.stock} units are available in stock.</p>}
          </div>
        );
      })}
    </div>
  );

  const DeliveryBlock = () => (
    <div className="rounded-2xl border border-slate-200 p-3" data-testid="delivery-block">
      <label className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Truck className="h-4 w-4 text-indigo-600" /> Delivery
        </span>
        <input
          data-testid="delivery-toggle"
          type="checkbox"
          checked={delivery.on}
          onChange={(e) => setDelivery({ ...delivery, on: e.target.checked, charge: e.target.checked ? delivery.charge : "" })}
          className="h-4 w-4"
        />
      </label>
      {delivery.on && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {(settings?.pos?.delivery_quick_amounts || [0, 20, 30, 50, 100]).map((a) => (
              <button
                key={a}
                data-testid={`delivery-quick-${a}`}
                onClick={() => setDelivery({ ...delivery, charge: a })}
                className={`h-9 rounded-lg border px-3 text-xs font-semibold ${
                  Number(delivery.charge) === a ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"
                }`}
              >
                {inr(a)}
              </button>
            ))}
          </div>
          <input
            data-testid="delivery-charge-input"
            type="number"
            min="0"
            step="1"
            value={delivery.charge}
            onChange={(e) => setDelivery({ ...delivery, charge: Math.max(0, Number(e.target.value) || 0) })}
            placeholder="Delivery Charge ₹"
            className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm tnum"
          />
          <input
            data-testid="delivery-address-input"
            value={delivery.address}
            onChange={(e) => setDelivery({ ...delivery, address: e.target.value })}
            placeholder="Delivery address"
            className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
          />
          <input
            data-testid="delivery-landmark-input"
            value={delivery.landmark}
            onChange={(e) => setDelivery({ ...delivery, landmark: e.target.value })}
            placeholder="Landmark"
            className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
          />
          <input
            data-testid="delivery-notes-input"
            value={delivery.notes}
            onChange={(e) => setDelivery({ ...delivery, notes: e.target.value })}
            placeholder="Delivery notes"
            className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
          />
          <p className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
            Products {inr(productTotal)} + Delivery {inr(Number(delivery.charge || 0))} ={" "}
            <b data-testid="delivery-grand-preview">{inr(grand)}</b>. Delivery is never counted towards the
            Notebook discount threshold.
          </p>
        </div>
      )}
    </div>
  );

  const AgentBlock = () => (
    <div className="rounded-2xl border border-slate-200 p-3" data-testid="billing-agent-block">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <User className="h-4 w-4 text-indigo-600" /> Billing Agent {!user && <span className="text-rose-600">*</span>}
      </p>
      {agentBlocked ? (
        <p data-testid="billing-agent-blocked" className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700">
          {agentBlocked}
        </p>
      ) : agentLocked ? (
        <div data-testid="billing-agent-locked" className="mt-2 flex h-11 items-center rounded-xl bg-slate-50 px-3 text-sm font-semibold text-slate-700">
          {agentName || "—"}
        </div>
      ) : (
        <select
          data-testid="billing-agent-select"
          value={agentId}
          onChange={(e) => {
            setAgentId(e.target.value);
            setAgentName(agents.find((a) => a.id === e.target.value)?.agent_name || "");
          }}
          className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm"
        >
          <option value="">{user && agentName ? `${agentName} (my account)` : "Select billing agent…"}</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>{a.agent_name}</option>
          ))}
        </select>
      )}
      {!user && !agents.length && (
        <p className="mt-2 text-[11px] text-amber-700">
          No billing agent is configured yet. Ask the administrator to add one in Admin → Billing Agents.
        </p>
      )}
    </div>
  );

  const DiscountBlock = () => {
    const ctx = discountCtx || {};
    const limits = ctx.limits || {};
    const err = quote?.manual_discount_error || "";
    const applied = Number(quote?.manual_discount || 0);
    if (!ctx.can_apply) return null;
    return (
      <div className="rounded-2xl border border-slate-200 p-3" data-testid="manual-discount-block">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Percent className="h-4 w-4 text-emerald-600" /> Manual Discount
        </p>
        <div className="mt-2 flex gap-2">
          {[["fixed", "₹ Amount", ctx.can_fixed], ["percent", "% Percent", ctx.can_percent]]
            .filter(([, , ok]) => ok)
            .map(([m, label]) => (
              <button
                key={m}
                data-testid={`manual-discount-mode-${m}`}
                onClick={() => setDiscount({ ...discount, mode: m })}
                className={`h-10 flex-1 rounded-xl border text-xs font-semibold ${
                  discount.mode === m ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 text-slate-600"
                }`}
              >
                {label}
              </button>
            ))}
        </div>
        <input
          data-testid="manual-discount-value-input"
          type="number"
          min="0"
          value={discount.value}
          onChange={(e) => setDiscount({ ...discount, value: e.target.value })}
          placeholder={discount.mode === "percent" ? "Discount %" : "Discount ₹"}
          className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm tnum outline-none focus:border-emerald-400"
        />
        {(limits.require_reason !== false || Number(discount.value) > 0 || discount.reason) && (
          <input
            data-testid="manual-discount-reason-input"
            value={discount.reason}
            onChange={(e) => setDiscount({ ...discount, reason: e.target.value })}
            placeholder={limits.require_reason === false ? "Reason for discount (optional)" : "Reason for discount *"}
            className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-emerald-400"
          />
        )}
        <p data-testid="manual-discount-limit-note" className="mt-2 text-[11px] text-slate-500">
          {user?.role === "admin"
            ? "Limit: unlimited (administrator)"
            : `Limit: ${Number(limits.max_percent_per_bill || 0)}% · ₹${Number(limits.max_fixed_per_bill || 0)} per bill${
                Number(limits.max_fixed_per_day || 0) > 0
                  ? ` · ₹${Number(limits.max_fixed_per_day)} per day (used ₹${Number(
                      quote?.manual_discount_used_today || ctx.used_today || 0
                    )})`
                  : ""
              }`}
        </p>
        {err && (
          <p data-testid="manual-discount-error" className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700">
            {err}
          </p>
        )}
        {!err && applied > 0 && (
          <p data-testid="manual-discount-applied" className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-700">
            Manual discount {inr(applied)} applied
            {Number(quote?.manual_discount_percent) > 0 ? ` (${quote.manual_discount_percent}%)` : ""}
          </p>
        )}
      </div>
    );
  };

  const Totals = () => (
    <div className="space-y-1.5 text-sm">
      {[
        ["Gross Subtotal", quote?.subtotal],
        ["Discount Eligible", quote?.eligible_subtotal],
        ["Non-Discountable", quote?.non_discountable_subtotal],
        ["Auto Discount", quote?.auto_discount, "text-emerald-700"],
        ["Product Net", quote?.product_net],
        ...(Number(quote?.manual_discount) > 0
          ? [["Manual Discount", quote?.manual_discount, "text-emerald-700"]]
          : []),
        ["Discounted Amount", quote?.amount_after_discount],
        ...(quote?.tax_amount ? [["Tax", quote?.tax_amount]] : []),
        ...(quote?.round_off ? [["Round Off", quote?.round_off]] : []),
        ...(delivery.on ? [["Delivery Charge", Number(delivery.charge || 0), "text-indigo-700"]] : []),
        ...(cardFee.amount > 0 ? [[`${cardFee.type} Fee (${cardFee.rate}%)`, cardFee.amount, "text-amber-700"]] : []),
      ].map(([label, value, tone]) => (
        <div key={label} className="flex justify-between">
          <span className="text-slate-500">{label}</span>
          <span
            data-testid={`totals-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`tnum font-medium ${tone || "text-slate-800"}`}
          >
            {inr(value)}
          </span>
        </div>
      ))}
      {!!(quote?.auto_discount_rules || []).length && (
        <p data-testid="auto-discount-rules" className="rounded-xl bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
          Applied: {quote.auto_discount_rules.join(" · ")}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
        <span className="font-outfit font-semibold text-slate-900">Grand Total</span>
        <span data-testid="pos-grand-total" className="font-outfit text-xl font-bold tnum text-indigo-700">
          {inr(grand)}
        </span>
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-slate-500">Amount Paid</span>
        <span data-testid="pos-amount-paid-display" className="tnum">{inr(paidAmount)}</span>
      </div>
      <div className="flex justify-between text-xs">
        <span data-testid="pos-balance-label" className="text-slate-500">{change > 0 ? "Change Due" : "Balance / Due"}</span>
        <span data-testid="pos-balance-value" className="tnum font-semibold text-amber-700">{inr(change > 0 ? change : balance)}</span>
      </div>
    </div>
  );

  const PaymentDetailsBlock = () => (
    <div className="space-y-2" data-testid="payment-details-block">
      {activeMethods.includes("UPI") && (
        <div className="rounded-xl border border-slate-200 p-3">
          <p className="mb-2 text-xs font-semibold text-slate-700">UPI transaction details *</p>
          <select
            data-testid="upi-provider-select"
            value={payDetails.UPI?.provider || ""}
            onChange={(e) => setDetail("UPI", "provider", e.target.value)}
            className="mb-2 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm"
          >
            <option value="">Select QR bank / provider…</option>
            {(payCfg.upi_providers || []).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input
            data-testid="upi-utr-input"
            value={payDetails.UPI?.utr_reference || ""}
            onChange={(e) => setDetail("UPI", "utr_reference", e.target.value)}
            placeholder="UTR / transaction reference number"
            className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm"
          />
        </div>
      )}
      {activeMethods.includes("Card") && (
        <div className="rounded-xl border border-slate-200 p-3">
          <p className="mb-2 text-xs font-semibold text-slate-700">Card transaction details *</p>
          <div className="mb-2 grid grid-cols-2 gap-2">
            {(payCfg.card_types || ["Credit Card", "Debit Card"]).map((t) => (
              <button
                key={t}
                data-testid={`card-type-${t.split(" ")[0].toLowerCase()}`}
                onClick={() => setDetail("Card", "card_type", t)}
                className={`h-11 rounded-xl border text-xs font-semibold ${
                  payDetails.Card?.card_type === t ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 text-slate-600"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="card-batch-input"
              value={payDetails.Card?.card_batch_number || ""}
              onChange={(e) => setDetail("Card", "card_batch_number", e.target.value)}
              placeholder="Batch number"
              className="h-11 rounded-xl border border-slate-200 px-3 text-sm"
            />
            <input
              data-testid="card-last4-input"
              value={payDetails.Card?.card_last4 || ""}
              onChange={(e) => setDetail("Card", "card_last4", e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Last 4 digits"
              inputMode="numeric"
              className="h-11 rounded-xl border border-slate-200 px-3 text-sm tnum"
            />
          </div>
          <p className="mt-2 text-[10px] text-slate-500">We never ask for or store the full card number, CVV or PIN.</p>
        </div>
      )}
      {activeMethods.includes("Bank Transfer") && (
        <div className="rounded-xl border border-slate-200 p-3">
          <p className="mb-2 text-xs font-semibold text-slate-700">Bank transfer details *</p>
          <select
            data-testid="transfer-mode-select"
            value={payDetails["Bank Transfer"]?.bank_transfer_mode || ""}
            onChange={(e) => setDetail("Bank Transfer", "bank_transfer_mode", e.target.value)}
            className="mb-2 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm"
          >
            <option value="">Select mode of transfer…</option>
            {(payCfg.transfer_modes || []).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <div className="grid gap-2 sm:grid-cols-2">
            <input data-testid="transfer-holder-input" value={payDetails["Bank Transfer"]?.account_holder_name || ""} onChange={(e) => setDetail("Bank Transfer", "account_holder_name", e.target.value)} placeholder="Account holder name" className="h-11 rounded-xl border border-slate-200 px-3 text-sm" />
            <input data-testid="transfer-bank-input" value={payDetails["Bank Transfer"]?.bank_name || ""} onChange={(e) => setDetail("Bank Transfer", "bank_name", e.target.value)} placeholder="Bank name" className="h-11 rounded-xl border border-slate-200 px-3 text-sm" />
            <input data-testid="transfer-customer-input" value={payDetails["Bank Transfer"]?.customer_name_snapshot ?? customer.name} onChange={(e) => setDetail("Bank Transfer", "customer_name_snapshot", e.target.value)} placeholder="Customer name" className="h-11 rounded-xl border border-slate-200 px-3 text-sm" />
            <input data-testid="transfer-mobile-input" value={payDetails["Bank Transfer"]?.customer_mobile_snapshot ?? customer.mobile} onChange={(e) => setDetail("Bank Transfer", "customer_mobile_snapshot", e.target.value)} placeholder="Customer mobile" className="h-11 rounded-xl border border-slate-200 px-3 text-sm" />
            {payDetails["Bank Transfer"]?.bank_transfer_mode === "Cheque" ? (
              <>
                <input data-testid="cheque-number-input" value={payDetails["Bank Transfer"]?.cheque_number || ""} onChange={(e) => setDetail("Bank Transfer", "cheque_number", e.target.value)} placeholder="Cheque number" className="h-11 rounded-xl border border-slate-200 px-3 text-sm" />
                <input data-testid="cheque-date-input" type="date" value={payDetails["Bank Transfer"]?.cheque_date || ""} onChange={(e) => setDetail("Bank Transfer", "cheque_date", e.target.value)} className="h-11 rounded-xl border border-slate-200 px-3 text-sm" />
              </>
            ) : (
              <input data-testid="transfer-reference-input" value={payDetails["Bank Transfer"]?.transaction_reference || ""} onChange={(e) => setDetail("Bank Transfer", "transaction_reference", e.target.value)} placeholder={payDetails["Bank Transfer"]?.bank_transfer_mode === "Cash Deposit" ? "Deposit reference" : "IMPS / NEFT reference number"} className="h-11 rounded-xl border border-slate-200 px-3 text-sm sm:col-span-2" />
            )}
          </div>
        </div>
      )}
      {cardFee.amount > 0 && (
        <p data-testid="card-fee-line" className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
          {cardFee.type} Transaction Fee ({cardFee.rate}%): {inr(cardFee.amount)}
        </p>
      )}
    </div>
  );

  const PaymentPanel = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {PAYMENTS.map((m) => (
          <button
            key={m}
            data-testid={`payment-${m.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setMethod(m)}
            className={`h-12 rounded-xl border text-xs font-semibold transition-colors ${
              method === m ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600"
            }`}
          >
            {m}
          </button>
        ))}
      </div>
      {method === "Mixed" ? (
        <div className="space-y-2">
          {splits.map((s, i) => (
            <div key={i} className="flex gap-2">
              <select
                data-testid={`split-method-${i}`}
                value={s.method}
                onChange={(e) => setSplits(splits.map((x, j) => (j === i ? { ...x, method: e.target.value } : x)))}
                className="h-11 flex-1 rounded-xl border border-slate-200 px-2 text-sm"
              >
                {["Cash", "UPI", "Card", "Bank Transfer"].map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
              <input
                data-testid={`split-amount-${i}`}
                value={s.amount}
                onChange={(e) => setSplits(splits.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                placeholder="Amount"
                className="h-11 w-28 rounded-xl border border-slate-200 px-3 text-sm tnum"
              />
              {splits.length > 1 && (
                <button onClick={() => setSplits(splits.filter((_, j) => j !== i))} className="text-slate-400">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <button
            data-testid="add-split-btn"
            onClick={() => setSplits([...splits, { method: "UPI", amount: "" }])}
            className="text-xs font-semibold text-indigo-700"
          >
            + Add payment mode
          </button>
        </div>
      ) : (
        <input
          data-testid="amount-paid-input"
          value={paid}
          onChange={(e) => setPaid(e.target.value)}
          placeholder="Amount received"
          className="h-12 w-full rounded-xl border border-slate-200 px-4 text-sm tnum outline-none focus:border-indigo-400"
        />
      )}
      {PaymentDetailsBlock()}
      {method === "Credit" && (
        <input
          data-testid="due-date-input"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm"
        />
      )}
      <button
        data-testid="complete-bill-btn"
        disabled={busy || !cart.length || overselling || !!quote?.manual_discount_error}
        onClick={completeBill}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 font-outfit text-base font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
        Complete Bill · {inr(grand)}      </button>
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8FAFC] pb-28 lg:pb-0">
      <header className="glass-bar sticky top-0 z-30 flex flex-wrap items-center gap-2 px-3 py-3 sm:gap-3 sm:px-4">
        <button data-testid="pos-home-btn" onClick={() => navigate("/")} className="text-slate-500 hover:text-indigo-700">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <BdsLogo size={30} showText={false} />
          <div className="hidden min-w-0 sm:block">
            <p className="truncate font-outfit text-sm font-bold leading-tight text-indigo-700 sm:text-base">Bharat Digital Seva</p>
            <p className="truncate text-[10px] text-slate-500">Billing Counter · {new Date().toLocaleDateString("en-IN")}</p>
          </div>
        </div>
        <div className="order-last flex w-full justify-center sm:order-none sm:w-auto sm:flex-1">
          <span className="hidden sm:inline-flex"><IstClock /></span>
          <span className="inline-flex sm:hidden"><IstClock compact /></span>
        </div>
        <span
          data-testid="network-status"
          className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            online ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
          }`}
        >
          {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {online ? "Online" : "Offline"}
        </span>
        {pending > 0 && (
          <button
            data-testid="sync-pending-btn"
            onClick={() => setShowPending(true)}
            className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {pending} pending sync
          </button>
        )}
        {fromCache && (
          <span data-testid="offline-cache-badge" className="hidden items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600 sm:flex">
            Offline catalog
          </span>
        )}
        <button
          data-testid="pos-scan-qr-btn"
          onClick={() => setShowScanner(true)}
          className="flex h-10 items-center gap-1.5 rounded-xl bg-indigo-600 px-3 text-xs font-semibold text-white"
        >
          <ScanLine className="h-4 w-4" /> <span className="hidden sm:inline">Scan QR</span>
        </button>
        <button
          data-testid="pos-stock-btn"
          onClick={() => setStockView((v) => !v)}
          className="flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600"
        >
          <Boxes className="h-4 w-4" /> <span className="hidden sm:inline">Stock</span>
        </button>
        <button
          data-testid="pos-stock-audit-btn"
          onClick={() => setAuditOpen(true)}
          className="flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600"
        >
          <ClipboardCheck className="h-4 w-4" /> <span className="hidden sm:inline">Stock Audit</span>
        </button>
        <button
          data-testid="pos-today-bills-btn"
          onClick={loadTodayBills}
          className="flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600"
        >
          <ReceiptText className="h-4 w-4" /> <span className="hidden sm:inline">Today's Bills</span>
        </button>
        <button
          data-testid="print-last-invoice-btn"
          disabled={!lastInvoice}
          onClick={() => printReceipt(lastInvoice, shop, printer, undefined, false)}
          className="flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 disabled:opacity-40"
        >
          <Printer className="h-4 w-4" /> <span className="hidden sm:inline">Last Invoice</span>
        </button>
      </header>

      <div className="grid gap-4 p-3 sm:p-4 lg:grid-cols-[1.55fr_1fr]">
        {/* left */}
        <div className="min-w-0 space-y-4">
          <div className="bds-card p-4">
            <div className="relative">
              <ScanLine className="pointer-events-none absolute left-4 top-4 h-5 w-5 text-indigo-400" />
              <input
                ref={searchRef}
                data-testid="pos-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder="Scan barcode / type product, SKU or STK code, then Enter"
                className="h-14 w-full rounded-xl border border-slate-200 bg-white pl-12 pr-4 text-base outline-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {["All", ...activeCategories].map((c) => (
                <button
                  key={c}
                  data-testid={`category-chip-${c}`}
                  onClick={() => setCategory(c)}
                  className={`shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                    category === c ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.slice(0, 60).map((p) => (
              <button
                key={p.id}
                data-testid={`product-card-${p.id}`}
                onClick={() => addToCart(p)}
                disabled={p.stock <= 0}
                className="bds-card p-4 text-left transition-transform active:scale-[0.98] disabled:opacity-50"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold leading-snug text-slate-900">{p.name}</p>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusTone(p.stock_status)}`}>
                    {p.stock}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  {p.category} · {p.stk_code || p.sku}
                </p>
                <p className="mt-2 font-outfit text-lg font-bold tnum text-indigo-700">{inr(p.selling_price)}</p>
                {p.discount_eligible && (
                  <span className="mt-1 inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                    Auto discount
                  </span>
                )}
              </button>
            ))}
            {!filtered.length && <p className="col-span-full py-10 text-center text-sm text-slate-400">No products match your search.</p>}
          </div>
        </div>

        {/* right */}
        <div className="hidden min-w-0 space-y-4 lg:block">
          <div className="bds-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-outfit font-semibold text-slate-900">Cart ({cart.length})</p>
              <div className="flex gap-2">
                <button data-testid="hold-bill-btn" onClick={holdBill} className="flex items-center gap-1 text-xs font-semibold text-amber-700">
                  <Pause className="h-3.5 w-3.5" /> Hold
                </button>
                <button data-testid="new-bill-btn" onClick={reset} className="flex items-center gap-1 text-xs font-semibold text-slate-500">
                  <X className="h-3.5 w-3.5" /> New Bill
                </button>
              </div>
            </div>
            <div className="max-h-[38vh] overflow-y-auto pr-1">
              {CartRows()}
            </div>
          </div>

          <div className="bds-card p-4">
            <p className="mb-3 flex items-center gap-2 font-outfit font-semibold text-slate-900">
              <User className="h-4 w-4 text-indigo-600" /> Customer
            </p>
            <input
              data-testid="customer-mobile-input"
              value={customer.mobile}
              onChange={(e) => lookupCustomer(e.target.value)}
              placeholder="Mobile number (auto lookup)"
              className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-indigo-400"
            />
            <input
              data-testid="customer-name-input"
              value={customer.name}
              onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
              placeholder="Customer name (optional)"
              className="mt-2 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-indigo-400"
            />
            {foundCustomer && (
              <p data-testid="customer-found-banner" className="mt-2 rounded-xl bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
                {foundCustomer.customer_id} · {foundCustomer.orders} orders · outstanding {inr(foundCustomer.outstanding)}
              </p>
            )}
          </div>

          <div className="bds-card p-4">
            <p className="mb-3 font-outfit font-semibold text-slate-900">
              Bill Summary {loadingQuote && <Loader2 className="ml-1 inline h-3.5 w-3.5 animate-spin text-indigo-500" />}
            </p>
            <div className="mb-3">{AgentBlock()}</div>

            {DeliveryBlock()}
            {DiscountBlock()}
            {Totals()}
            <div className="mt-4">
              {PaymentPanel()}
            </div>          </div>

          {held.length > 0 && (
            <div className="bds-card p-4">
              <p className="mb-2 font-outfit font-semibold text-slate-900">Held Bills</p>
              {held.map((h) => (
                <button
                  key={h.id}
                  data-testid={`resume-held-${h.id}`}
                  onClick={() => resumeBill(h)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm transition-colors hover:bg-indigo-50"
                >
                  <span>{h.label}</span>
                  <Play className="h-4 w-4 text-indigo-600" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* mobile sticky cart bar with prominent central Scan action */}
      <div className="glass-bar fixed bottom-0 left-0 right-0 z-40 flex items-center gap-2 px-3 py-2.5 lg:hidden">
        <div className="min-w-0 flex-1">
          <p data-testid="mobile-cart-count" className="text-[11px] text-slate-500">
            {cart.reduce((s, c) => s + c.qty, 0)} qty · {cart.length} items
          </p>
          <p data-testid="mobile-cart-total" className="font-outfit text-lg font-bold tnum text-indigo-700">{inr(grand)}</p>
        </div>
        <button
          data-testid="mobile-scan-qr-btn"
          onClick={() => {
            setScanMsg("");
            setShowScanner(true);
          }}
          className="flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-2xl bg-indigo-600 text-[9px] font-bold uppercase tracking-wide text-white shadow-lg shadow-indigo-600/30 active:scale-95"
        >
          <ScanLine className="h-6 w-6" /> Scan
        </button>
        <button
          data-testid="mobile-view-cart-btn"
          onClick={() => setShowCart(true)}
          className="flex h-12 shrink-0 items-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 text-sm font-semibold text-indigo-700"
        >
          <ShoppingCart className="h-4 w-4" /> Cart
        </button>
      </div>

      {showCart && (
        <div className="fixed inset-0 z-50 flex items-end bg-slate-900/40 lg:hidden" onClick={() => setShowCart(false)}>
          <div onClick={(e) => e.stopPropagation()} className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-outfit text-lg font-semibold">Cart & Payment</p>
              <button data-testid="close-cart-sheet" onClick={() => setShowCart(false)}>
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            {CartRows()}
            <div className="mt-4 space-y-2">
              <input
                data-testid="mobile-customer-mobile-input"
                value={customer.mobile}
                onChange={(e) => lookupCustomer(e.target.value)}
                placeholder="Customer mobile"
                className="h-12 w-full rounded-xl border border-slate-200 px-3 text-sm"
              />
              <input
                value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                placeholder="Customer name"
                className="h-12 w-full rounded-xl border border-slate-200 px-3 text-sm"
              />
            </div>
            <div className="mt-4">
              <div className="mb-3">{AgentBlock()}</div>

              {DeliveryBlock()}
              {DiscountBlock()}
              {Totals()}
            </div>
            <div className="mt-4 pb-6">
              {PaymentPanel()}
              <button onClick={holdBill} className="mt-3 h-12 w-full rounded-xl border border-amber-200 bg-amber-50 text-sm font-semibold text-amber-700">
                Hold Bill
              </button>
            </div>
          </div>
        </div>
      )}

      {showBills && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setShowBills(false)}>
          <div onClick={(e) => e.stopPropagation()} className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-outfit text-lg font-semibold">Today's Bills</p>
              <button data-testid="close-today-bills" onClick={() => setShowBills(false)}>
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            {todayBills.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No bills today yet.</p>}
            {todayBills.map((b) => (
              <div key={b.id} className="flex items-center justify-between border-b border-slate-100 py-2.5 text-sm">
                <div>
                  <p className="font-semibold text-slate-900">{b.invoice_no}</p>
                  <p className="text-xs text-slate-500">
                    {b.time} · {b.customer_name} · {b.payment_method}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tnum font-semibold">{inr(b.grand_total)}</span>
                  <button
                    data-testid={`reprint-${b.id}`}
                    onClick={() => printReceipt(b, shop, printer, undefined, false)}
                    className="rounded-lg border border-slate-200 p-2 text-slate-600"
                  >
                    <Printer className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <QrScanner
        open={showScanner}
        onClose={() => {
          setShowScanner(false);
          setScanMsg("");
        }}
        onResult={handleScan}
        cartCount={cart.reduce((s, c) => s + c.qty, 0)}
        cartTotal={quote?.grand_total || 0}
        lastMessage={scanMsg}
        autoCamera
      />
      <QrScanner
        open={auditScan}
        onClose={() => setAuditScan(false)}
        onResult={addAuditLine}
        title="Scan Shelf Product"
        allowContinuous={false}
      />

      {auditOpen && (
        <div className="fixed inset-0 z-[55] flex items-end justify-center bg-slate-900/45 p-0 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 sm:rounded-2xl" data-testid="stock-audit-modal">
            <div className="mb-1 flex items-center justify-between">
              <p className="flex items-center gap-2 font-outfit text-lg font-semibold">
                <ClipboardCheck className="h-5 w-5 text-indigo-600" /> Shelf Stock Audit
              </p>
              <button data-testid="close-stock-audit" onClick={() => setAuditOpen(false)}>
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <p className="mb-4 text-xs text-slate-500">
              Scan each shelf product and enter the quantity you counted. Counts are sent to the administrator
              for approval — stock is never changed from the counter.
            </p>
            <button
              data-testid="audit-scan-btn"
              onClick={() => setAuditScan(true)}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 text-sm font-semibold text-white"
            >
              <ScanLine className="h-4 w-4" /> Scan Product QR
            </button>
            <select
              data-testid="audit-add-select"
              value=""
              onChange={(e) => {
                const p = products.find((x) => x.id === e.target.value);
                if (p && !auditLines.some((l) => l.product_oid === p.id))
                  setAuditLines([...auditLines, { product_oid: p.id, name: p.name, stk_code: p.stk_code, counted: "" }]);
              }}
              className="mt-3 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm"
            >
              <option value="">Or pick a product manually…</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <div className="mt-4 space-y-2">
              {auditLines.map((l, i) => (
                <div key={l.product_oid} className="flex items-center gap-2 rounded-xl border border-slate-200 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{l.name}</p>
                    <p className="text-[11px] text-slate-500">{l.stk_code}</p>
                  </div>
                  <input
                    data-testid={`audit-count-${l.product_oid}`}
                    type="number"
                    min="0"
                    value={l.counted}
                    onChange={(e) => setAuditLines(auditLines.map((x, j) => (j === i ? { ...x, counted: e.target.value } : x)))}
                    placeholder="Counted"
                    className="h-11 w-24 rounded-xl border border-slate-200 px-2 text-center text-sm tnum"
                  />
                  <button onClick={() => setAuditLines(auditLines.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {!auditLines.length && <p className="py-6 text-center text-sm text-slate-400">No products in this count sheet yet.</p>}
            </div>

            <button
              data-testid="submit-audit-btn"
              disabled={auditBusy || !auditLines.length}
              onClick={submitAudit}
              className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 text-sm font-semibold text-white disabled:opacity-50"
            >
              {auditBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Submit Count for Approval
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-slate-900/45 p-4">
          <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-2xl bg-white p-5" data-testid="bill-preview-modal">
            <div className="mb-2 flex items-center justify-between">
              <p className="font-outfit text-lg font-semibold text-emerald-700">Bill Completed</p>
              <button data-testid="close-bill-preview" onClick={() => setPreview(null)}>
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3 font-mono text-[11px] leading-tight">
              <p className="text-center font-bold">{shop?.name || "BHARAT DIGITAL SEVA"}</p>
              <p className="text-center text-[10px] text-slate-500">{shop?.address}</p>
              <p className="mt-1">Invoice: {preview.invoice_no}</p>
              <p>{dmy(preview.date)} {preview.time} · {preview.payment_method}</p>
              <p>Billing Agent: {preview.billing_agent_name_snapshot || preview.cashier || "-"}</p>
              {(preview.payments || []).map((p, i) => {
                const d = p.details || {};
                const bits = [
                  d.provider,
                  d.card_type ? `${d.card_type}${d.card_last4 ? ` ****${d.card_last4}` : ""}` : "",
                  d.card_batch_number ? `Batch ${d.card_batch_number}` : "",
                  d.bank_transfer_mode,
                  d.utr_reference ? `UTR ${d.utr_reference}` : "",
                  d.transaction_reference ? `Ref ${d.transaction_reference}` : "",
                  d.cheque_number ? `Cheque ${d.cheque_number}` : "",
                ].filter(Boolean);
                return (
                  <p key={i} data-testid={`preview-payment-${i}`}>
                    {p.method} {inr(p.amount)}
                    {bits.length ? ` · ${bits.join(" · ")}` : ""}
                  </p>
                );
              })}
              {!!preview.card_fee_amount && (
                <p data-testid="preview-card-fee" className="font-semibold text-amber-700">
                  {preview.card_fee_card_type || "Card"} Fee ({preview.card_fee_rate}%): {inr(preview.card_fee_amount)}
                </p>
              )}
              <div className="my-1 border-t border-dashed border-slate-400" />
              {preview.items.map((i) => (
                <div key={i.product_oid} className="flex justify-between gap-2">
                  <span className="truncate">{i.qty} × {i.product_name}</span>
                  <span>{inr(i.line_total)}</span>
                </div>
              ))}
              <div className="my-1 border-t border-dashed border-slate-400" />
              <div className="flex justify-between"><span>Subtotal</span><span>{inr(preview.subtotal)}</span></div>
              {Number(preview.auto_discount) > 0 && (
                <div className="flex justify-between" data-testid="preview-auto-discount"><span>Auto Discount</span><span>- {inr(preview.auto_discount)}</span></div>
              )}
              {Number(preview.manual_discount) > 0 && (
                <div className="flex justify-between" data-testid="preview-manual-discount">
                  <span>Manual Discount{preview.manual_discount_percent ? ` (${preview.manual_discount_percent}%)` : ""}</span>
                  <span>- {inr(preview.manual_discount)}</span>
                </div>
              )}
              {!!preview.manual_discount_reason && (
                <p data-testid="preview-discount-reason" className="text-[10px] text-slate-500">Reason: {preview.manual_discount_reason}</p>
              )}
              {Number(preview.discount_amount) > 0 && (
                <div className="flex justify-between" data-testid="preview-discounted-amount"><span>Discounted Amount</span><span>{inr(preview.amount_after_discount)}</span></div>
              )}
              {Number(preview.delivery_charge) > 0 && (
                <div className="flex justify-between"><span>Delivery Charge</span><span>{inr(preview.delivery_charge)}</span></div>
              )}
              <div className="flex justify-between font-bold"><span>GRAND TOTAL</span><span>{inr(preview.grand_total)}</span></div>
              <div className="flex justify-between"><span>Paid</span><span>{inr(preview.amount_paid)}</span></div>
              {Number(preview.change_due) > 0 && <div className="flex justify-between"><span>Change</span><span>{inr(preview.change_due)}</span></div>}
              {Number(preview.due_amount) > 0 && <div className="flex justify-between"><span>Due</span><span>{inr(preview.due_amount)}</span></div>}
              <p className="mt-1 text-center text-[10px]">{shop?.invoice_footer}</p>
            </div>
            <div className="mt-4 grid gap-2">
              {preview.offline_pending && (
                <p data-testid="preview-offline-note" className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">
                  Offline bill — temporary number {preview.invoice_no}. The real invoice number is assigned
                  automatically when the internet returns.
                </p>
              )}
              <button
                data-testid="print-receipt-58-btn"
                onClick={() => {
                  fx.print("Preparing print…");
                  printReceipt(preview, shop, printer, printer.printer_type || "58mm", false);
                }}
                className="flex h-12 items-center justify-center gap-2 rounded-xl bg-emerald-600 text-sm font-semibold text-white"
              >
                <Printer className="h-4 w-4" /> Print {printer.printer_type || "58mm"} Receipt
              </button>
              {whatsappCfg.enabled !== false && !preview.offline_pending && (
                <button
                  data-testid="whatsapp-share-btn"
                  onClick={() => shareOnWhatsapp(preview, shop, whatsappCfg)}
                  className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#25D366] text-sm font-semibold text-white"
                >
                  <MessageCircle className="h-4 w-4" /> Send Bill on WhatsApp
                </button>
              )}
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="download-receipt-pdf-btn"
                  onClick={() => printReceipt(preview, shop, printer, printer.printer_type || "58mm", false)}
                  className="h-11 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700"
                >
                  Download PDF
                </button>
                <button
                  data-testid="print-a4-invoice-btn"
                  onClick={() => printInvoice(preview, shop, "A4", false, printer)}
                  className="h-11 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700"
                >
                  Print A4 Invoice
                </button>
              </div>
              <button
                data-testid="preview-new-bill-btn"
                onClick={() => setPreview(null)}
                className="h-11 rounded-xl bg-indigo-600 text-sm font-semibold text-white"
              >
                New Bill
              </button>
            </div>
          </div>
        </div>
      )}

      {showPending && (
        <div className="fixed inset-0 z-[58] flex items-center justify-center bg-slate-900/45 p-4" onClick={() => setShowPending(false)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="pending-sync-modal" className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-outfit text-lg font-semibold">Pending Sync</p>
              <button data-testid="close-pending-sync" onClick={() => setShowPending(false)}>
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <p className="mb-3 rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              These bills were made while the internet was down. They sync automatically when the connection
              returns — the temporary OFF numbers are then replaced by real invoice numbers.
            </p>
            <div className="space-y-2">
              {readQueue().map((entry, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
                  <span className="min-w-0">
                    <b className="text-sm">{entry.offline_no || "Offline bill"}</b>
                    <span className="block text-[11px] text-slate-500">
                      {entry.payload?.items?.length || 0} items · {entry.payload?.payment_method}
                      {entry.error ? ` · ${entry.error}` : ""}
                    </span>
                  </span>
                  <span className="shrink-0 tnum text-sm font-semibold">{inr(entry.grand_total || 0)}</span>
                </div>
              ))}
            </div>
            <button
              data-testid="sync-now-btn"
              disabled={syncing || !online}
              onClick={syncQueue}
              className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 text-sm font-semibold text-white disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : online ? "Sync Now" : "Waiting for internet…"}
            </button>
          </div>
        </div>
      )}

      {stockView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setStockView(false)}>
          <div onClick={(e) => e.stopPropagation()} className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-outfit text-lg font-semibold">Available Stock</p>
              <button data-testid="close-stock-view" onClick={() => setStockView(false)}>
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            {products.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm">
                <div>
                  <p className="font-medium text-slate-900">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.category} · {p.stk_code}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tnum">{inr(p.selling_price)}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusTone(p.stock_status)}`}>
                    {p.stock} {p.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
